import { type PriceObjectDto } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { LoggerService } from "@modules/common/logger/loggerService"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { type ProgramApiGateway } from "@modules/programs/gateway/programApiGateway"
import {
  UpdateProgramPriceInputDtoSchema,
  type UserProgramDetailsDto,
  UserProgramDetailsDtoSchema,
} from "@modules/programs/gateway/programDto"
import {
  type CreateUserProgramInputDto,
  type UpdateUserProgramInputDto,
} from "@modules/programs/gateway/programInputDto"
import { type UserProgramItemDto, UserProgramItemDtoSchema } from "@modules/programs/gateway/userProgramItemDto"
import { ProgramFilter, ProgramType } from "@modules/programs/types/programTypes"
import { type Page, type Paginated, toPage } from "@modules/utils/paginated"
import { type ListOrder } from "@modules/utils/types"

export class ProgramApi implements ProgramApiGateway {
  private baseUrl = "/programs/user-programs"
  private programBaseUrl = "/programs"
  constructor(private api: ApiClient) {}

  /* ***************************************************** */
  /*        user-program-controller                       */
  /* ***************************************************** */
  async getProgram(id: string): Promise<UserProgramDetailsDto> {
    const resultDto = await this.api.get<UserProgramDetailsDto>(`${this.baseUrl}/${id}`)
    return resultDto.data
  }

  async getProgramForBox(id: string): Promise<UserProgramDetailsDto> {
    const resultDto = await this.api.get<UserProgramDetailsDto>(`${this.baseUrl}/pro/box/${id}`)
    return resultDto.data
  }

  async getPrograms(
    filter: ProgramFilter,
    order: ListOrder,
    page = 0,
    size = 1000,
    onlyWithPrice = false,
  ): Promise<Page<UserProgramItemDto>> {
    const result = await this.api.get<Paginated<UserProgramItemDto>>(`${this.baseUrl}`, {
      params: {
        page,
        size,
        onlyWithPrice,
        filter,
        order,
      },
    })
    const pageResult = toPage(result.data)

    return {
      ...pageResult,
      content: pageResult.content
        .filter((program) => program.type !== ProgramType.IN_BOX)
        .map((program) => LoggerService.parseApiDto(UserProgramItemDtoSchema, program)),
    }
  }

  async createProgram(programInput: CreateUserProgramInputDto): Promise<UserProgramDetailsDto> {
    const result = await this.api.post<UserProgramDetailsDto>(`${this.baseUrl}`, programInput)
    return LoggerService.parseApiDto(UserProgramDetailsDtoSchema, result.data)
  }

  async updateProgram(programId: string, programInput: UpdateUserProgramInputDto): Promise<UserProgramDetailsDto> {
    const result = await this.api.put<UserProgramDetailsDto>(`${this.baseUrl}/${programId}`, programInput)
    return LoggerService.parseApiDto(UserProgramDetailsDtoSchema, result.data)
  }

  async deleteProgram(programId: string): Promise<void> {
    await this.api.delete<void>(`${this.baseUrl}/${programId}`)
  }

  /* ***************************************************** */
  /*        program-price-controller                       */
  /* ***************************************************** */
  async updateProgramPrice(programId: string, price: PriceObjectDto, trialDuration: number | null) {
    const inputDto = LoggerService.parseApiDto(UpdateProgramPriceInputDtoSchema, { price, trialDuration })
    const result = await this.api.post<UserProgramDetailsDto>(`${this.programBaseUrl}/${programId}/price`, inputDto)

    return LoggerService.parseApiDto(UserProgramDetailsDtoSchema, result.data)
  }

  async removeProgramPrice(programId: string) {
    const result = await this.api.delete<UserProgramDetailsDto>(`${this.programBaseUrl}/${programId}/price`)
    return LoggerService.parseApiDto(UserProgramDetailsDtoSchema, result.data)
  }
}
